import { Empty } from 'antd'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { cx } from '@/Ship/Helpers/cssClass'
import { useNotification } from '@/Ship/Hooks/useNotification'

import { AddTaskModal } from '../Components/Modals/AddTaskModal'
import { ConfirmExecutionModal } from '../Components/Modals/ConfirmExecutionModal'
import { DelegateExecutorModal } from '../Components/Modals/DelegateExecutorModal'
import { ExecutorMessageModal } from '../Components/Modals/ExecutorMessageModal'
import { PushModal } from '../Components/Modals/PushModal'
import { AddTaskModalStore } from '../Stores/AddTaskModalStore'
import { ConfirmExecutionModalStore } from '../Stores/ConfirmExecutionModalStore'
import { DelegateExecutorModalStore } from '../Stores/DelegateExecutorModalStore'
import { ExecutorMessageModalStore } from '../Stores/ExecutorMessageModalStore'
import { PushModalStore } from '../Stores/PushModalStore'
import type { VehicleNeedsEditStore } from '../Stores/VehicleNeedsEditStore'
import { VehicleNeedsViewStore } from '../Stores/VehicleNeedsViewStore'

import { VehicleNeedCard } from './VehicleNeedCard'
import type { ExecutorModalOpeners } from './ExecutorBlock'

import styles from './RequestFormTemplate.module.css'

// Стори модалок — на рівні модуля: ядро перемонтовує шаблон картки (зокрема у PWA),
// і стор у useState скидав би `open` — модалка блимала й закривалась. Одночасно
// відкрита лише одна заявка, тож спільний екземпляр безпечний.
const confirmStore = new ConfirmExecutionModalStore()
const pushStore = new PushModalStore()
const executorMessageStore = new ExecutorMessageModalStore()
const delegateStore = new DelegateExecutorModalStore()
/** Стабільний порожній список: `?? []` давав би новий масив на кожен рендер і перезапускав ефект */
const NO_NEEDS: any[] = []

interface VehicleNeedsProps {
    formContext: any
    editStore: VehicleNeedsEditStore
}

/**
 * Потреби заявки з виконавцями й завданнями в обох режимах. У редагуванні локальні зміни
 * завдань живуть в `editStore` і зберігаються разом із формою (`subscribeFormEvents`).
 */
export const VehicleNeeds = observer(({ formContext, editStore }: VehicleNeedsProps) => {
    const crudContext = useCrudResourceContext()
    const { showSuccess, showError } = useNotification()
    const [viewStore] = useState(() => new VehicleNeedsViewStore())
    const [addTaskStore] = useState(() => new AddTaskModalStore())

    const mode: 'view' | 'edit' = formContext?.isEditable === false ? 'view' : 'edit'
    const vehicleNeeds: any[] = formContext?.values?.vehicleNeeds ?? NO_NEEDS
    const requestCancelled = formContext?.values?.status === 'cancelled'

    const notify = useMemo(() => ({ success: showSuccess, error: showError }), [showSuccess, showError])
    const reload = useCallback(() => crudContext?.actions?.reloadData?.(), [crudContext])

    // Редагування: локальні списки завдань — з найсвіжіших vehicleNeeds; збереження — з формою
    useEffect(() => {
        if (mode !== 'edit') return undefined
        editStore.syncFromVehicleNeeds(vehicleNeeds)
        return editStore.subscribeFormEvents(formContext?.eventManager, notify)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, editStore, vehicleNeeds, formContext?.eventManager])

    const openers: ExecutorModalOpeners = useMemo(
        () => ({
            confirmExecution: (task) => confirmStore.openFor(task),
            sendPush: (task) => pushStore.openFor(task),
            sendMessage: (ex) => {
                if (!executorMessageStore.openFor(ex)) {
                    showError('Немає водіїв', 'У виконавця ще немає призначених водіїв або всі завдання завершено')
                }
            },
            delegate: (ex) => delegateStore.openFor(ex.uuid, ex.dispatcher?.uuid),
            addTask: (executorUuid) => addTaskStore.openFor(executorUuid),
        }),
        [addTaskStore, showError]
    )

    return (
        <>
            {vehicleNeeds.length === 0 ? (
                <Empty description="Потреб у транспорті немає" />
            ) : (
                <div className={cx(styles, 'needs')}>
                    {vehicleNeeds.map((vn: any, index: number) => (
                        <VehicleNeedCard
                            key={vn.uuid || index}
                            mode={mode}
                            index={index}
                            vn={vn}
                            viewStore={viewStore}
                            editStore={editStore}
                            requestCancelled={requestCancelled}
                            notify={notify}
                            reload={reload}
                            openers={openers}
                        />
                    ))}
                </div>
            )}

            {mode === 'view' && (
                <>
                    <ConfirmExecutionModal store={confirmStore} />
                    <PushModal store={pushStore} />
                    <ExecutorMessageModal store={executorMessageStore} />
                    <DelegateExecutorModal store={delegateStore} />
                </>
            )}
            {mode === 'edit' && <AddTaskModal store={addTaskStore} editStore={editStore} vehicleNeeds={vehicleNeeds} />}
        </>
    )
})
